import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit, Trash2, Shield, Loader2, Check, X, 
  Users, Calendar, Image, FileText, Ticket, Crown,
  Settings, LayoutDashboard, Sparkles, MessageSquare,
  ChevronDown, Eye, Lock, Unlock, Mail, ShieldCheck,
  Store, Building2, Scale, Search, Copy, MoreVertical,
  CheckCircle2, XCircle, AlertTriangle, UserPlus, Layers,
  Star, Zap, UserCheck, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { rolesApi, usersApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const PERMISSION_GROUPS = {
  'Dashboard': {
    icon: LayoutDashboard,
    color: '#EF4444',
    description: 'Acceso al panel principal y métricas',
    permissions: [
      { key: 'dashboard_view', label: 'Ver dashboard', desc: 'Panel principal' },
      { key: 'dashboard_stats', label: 'Ver estadísticas', desc: 'Métricas completas' },
    ]
  },
  'Eventos': {
    icon: Calendar,
    color: '#D4AF37',
    description: 'Gestión completa de eventos',
    permissions: [
      { key: 'events_view', label: 'Ver eventos', desc: 'Acceso a lista de eventos' },
      { key: 'events_create', label: 'Crear eventos', desc: 'Crear nuevos eventos' },
      { key: 'events_edit', label: 'Editar eventos', desc: 'Modificar eventos existentes' },
      { key: 'events_delete', label: 'Eliminar eventos', desc: 'Borrar eventos' },
      { key: 'events_publish', label: 'Publicar/Despublicar', desc: 'Cambiar estado de publicación' },
      { key: 'events_force_active', label: 'Forzar activo', desc: 'Mantener eventos activos' },
      { key: 'events_manage_tiers', label: 'Gestionar tandas', desc: 'Administrar entradas' },
      { key: 'events_manage_discounts', label: 'Códigos descuento', desc: 'Gestionar descuentos' },
    ]
  },
  'Banner/Destacados': {
    icon: Star,
    color: '#F59E0B',
    description: 'Banners y eventos destacados',
    permissions: [
      { key: 'banner_view', label: 'Ver banners', desc: 'Acceso a destacados' },
      { key: 'banner_create', label: 'Crear banners', desc: 'Crear destacados' },
      { key: 'banner_edit', label: 'Editar banners', desc: 'Modificar destacados' },
      { key: 'banner_delete', label: 'Eliminar banners', desc: 'Borrar destacados' },
    ]
  },
  'Tickets/Ventas': {
    icon: Ticket,
    color: '#F59E0B',
    description: 'Control de ventas y validación',
    permissions: [
      { key: 'tickets_view', label: 'Ver ventas y tickets', desc: 'Acceso a ventas' },
      { key: 'tickets_validate', label: 'Validar tickets', desc: 'Escanear entradas' },
      { key: 'tickets_export', label: 'Exportar reportes', desc: 'Descargar informes' },
      { key: 'tickets_refund', label: 'Procesar reembolsos', desc: 'Gestionar devoluciones' },
    ]
  },
  'FULL BRAVX': {
    icon: Crown,
    color: '#D4AF37',
    description: 'Suscripciones premium',
    permissions: [
      { key: 'fullbravx_view', label: 'Ver suscripciones', desc: 'Listado de suscriptores' },
      { key: 'fullbravx_manage', label: 'Gestionar suscripciones', desc: 'Activar/cancelar' },
      { key: 'fullbravx_coupons', label: 'Crear cupones', desc: 'Códigos de descuento' },
    ]
  },
  'Usuarios Públicos': {
    icon: Users,
    color: '#06B6D4',
    description: 'Gestión de usuarios registrados',
    permissions: [
      { key: 'public_users_view', label: 'Ver usuarios', desc: 'Lista de registrados' },
      { key: 'public_users_edit', label: 'Editar usuarios', desc: 'Modificar datos' },
      { key: 'public_users_subscription', label: 'Gestionar suscripción', desc: 'Cambiar estado premium' },
    ]
  },
  'Solicitudes DJ': {
    icon: Zap,
    color: '#8B5CF6',
    description: 'Gestión de solicitudes de DJs',
    permissions: [
      { key: 'dj_requests_view', label: 'Ver solicitudes', desc: 'Lista de solicitudes DJ' },
      { key: 'dj_requests_approve', label: 'Aprobar/Rechazar', desc: 'Decidir solicitudes' },
      { key: 'dj_requests_manage', label: 'Gestionar DJs', desc: 'Administrar aprobados' },
    ]
  },
  'Partners': {
    icon: Store,
    color: '#22C55E',
    description: 'Negocios aliados y beneficios',
    permissions: [
      { key: 'partners_view', label: 'Ver partners', desc: 'Lista de partners' },
      { key: 'partners_create', label: 'Crear partners', desc: 'Agregar nuevos' },
      { key: 'partners_edit', label: 'Editar partners', desc: 'Modificar datos' },
      { key: 'partners_delete', label: 'Eliminar partners', desc: 'Borrar partners' },
      { key: 'partners_manage_benefits', label: 'Gestionar beneficios', desc: 'Administrar ofertas' },
    ]
  },
  'Aprobar Beneficios': {
    icon: CheckCircle2,
    color: '#10B981',
    description: 'Aprobación de solicitudes de beneficios',
    permissions: [
      { key: 'benefits_view', label: 'Ver solicitudes', desc: 'Lista de solicitudes' },
      { key: 'benefits_approve', label: 'Aprobar/Rechazar', desc: 'Decidir solicitudes' },
    ]
  },
  'Productoras': {
    icon: Building2,
    color: '#A855F7',
    description: 'Productoras de eventos',
    permissions: [
      { key: 'producers_view', label: 'Ver productoras', desc: 'Lista de productoras' },
      { key: 'producers_manage', label: 'Gestionar productoras', desc: 'Administrar productoras' },
      { key: 'producers_approve', label: 'Aprobar solicitudes', desc: 'Aprobar nuevas' },
      { key: 'producers_edit', label: 'Editar productoras', desc: 'Modificar datos' },
    ]
  },
  'Prensa': {
    icon: FileText,
    color: '#3B82F6',
    description: 'Medios de prensa',
    permissions: [
      { key: 'press_view', label: 'Ver prensa', desc: 'Lista de medios' },
      { key: 'press_manage', label: 'Gestionar prensa', desc: 'Administrar medios' },
      { key: 'press_approve', label: 'Aprobar solicitudes', desc: 'Aprobar nuevos' },
      { key: 'press_edit', label: 'Editar prensa', desc: 'Modificar datos' },
    ]
  },
  'Contratos': {
    icon: Scale,
    color: '#6366F1',
    description: 'Contratos entre prensa y productoras',
    permissions: [
      { key: 'contracts_view', label: 'Ver contratos', desc: 'Lista de contratos' },
      { key: 'contracts_manage', label: 'Gestionar contratos', desc: 'Administrar contratos' },
      { key: 'contracts_view_chat', label: 'Ver chats', desc: 'Leer conversaciones' },
      { key: 'contracts_cancel', label: 'Cancelar contratos', desc: 'Cancelar contratos' },
      { key: 'contracts_process_cancellation', label: 'Procesar cancelaciones', desc: 'Aprobar/rechazar' },
    ]
  },
  'Moderación': {
    icon: ShieldCheck,
    color: '#EF4444',
    description: 'Moderación y control de usuarios',
    permissions: [
      { key: 'moderation_view', label: 'Ver moderación', desc: 'Panel de moderación' },
      { key: 'moderation_manage', label: 'Gestionar moderación', desc: 'Administrar reportes' },
      { key: 'moderation_strikes', label: 'Aplicar strikes', desc: 'Penalizar usuarios' },
      { key: 'moderation_ban', label: 'Banear usuarios', desc: 'Suspender cuentas' },
      { key: 'moderation_view_chats', label: 'Ver todos los chats', desc: 'Monitorear conversaciones' },
      { key: 'moderation_flag_messages', label: 'Marcar mensajes', desc: 'Reportar contenido' },
    ]
  },
  'Retiros': {
    icon: CreditCard,
    color: '#059669',
    description: 'Solicitudes de retiro de dinero',
    permissions: [
      { key: 'withdrawals_view', label: 'Ver retiros', desc: 'Lista de solicitudes' },
      { key: 'withdrawals_approve', label: 'Aprobar/Rechazar', desc: 'Decidir solicitudes' },
      { key: 'withdrawals_process', label: 'Procesar pagos', desc: 'Ejecutar transferencias' },
    ]
  },
  'Galería': {
    icon: Image,
    color: '#10B981',
    description: 'Fotos y videos de eventos',
    permissions: [
      { key: 'gallery_view', label: 'Ver galería', desc: 'Acceso a fotos y videos' },
      { key: 'gallery_upload', label: 'Subir contenido', desc: 'Cargar archivos' },
      { key: 'gallery_delete', label: 'Eliminar contenido', desc: 'Borrar archivos' },
    ]
  },
  'Auras': {
    icon: Sparkles,
    color: '#A855F7',
    description: 'Estilos visuales de eventos',
    permissions: [
      { key: 'auras_view', label: 'Ver auras', desc: 'Lista de estilos' },
      { key: 'auras_create', label: 'Crear auras', desc: 'Diseñar nuevos' },
      { key: 'auras_edit', label: 'Editar auras', desc: 'Modificar estilos' },
      { key: 'auras_delete', label: 'Eliminar auras', desc: 'Borrar estilos' },
    ]
  },
  'Notas & Chat': {
    icon: MessageSquare,
    color: '#EC4899',
    description: 'Comunicación interna del equipo',
    permissions: [
      { key: 'notes_view', label: 'Ver notas y chat', desc: 'Leer mensajes' },
      { key: 'notes_write', label: 'Escribir mensajes', desc: 'Enviar notas' },
      { key: 'notes_delete', label: 'Eliminar mensajes', desc: 'Borrar propios' },
    ]
  },
  'Calendario': {
    icon: Calendar,
    color: '#0EA5E9',
    description: 'Calendario interno del equipo',
    permissions: [
      { key: 'calendar_view', label: 'Ver calendario', desc: 'Acceso al calendario' },
      { key: 'calendar_create', label: 'Crear eventos', desc: 'Agregar al calendario' },
      { key: 'calendar_edit', label: 'Editar eventos', desc: 'Modificar eventos' },
      { key: 'calendar_delete', label: 'Eliminar eventos', desc: 'Borrar del calendario' },
    ]
  },
  'Archivos': {
    icon: FileText,
    color: '#6B7280',
    description: 'Archivos internos del equipo',
    permissions: [
      { key: 'files_view', label: 'Ver archivos', desc: 'Acceso a documentos' },
      { key: 'files_upload', label: 'Subir archivos', desc: 'Cargar documentos' },
      { key: 'files_delete', label: 'Eliminar archivos', desc: 'Borrar documentos' },
    ]
  },
  'Marketing': {
    icon: Mail,
    color: '#F97316',
    description: 'Campañas y comunicaciones',
    permissions: [
      { key: 'marketing_view', label: 'Ver marketing', desc: 'Lista para campañas' },
      { key: 'marketing_send', label: 'Enviar emails', desc: 'Enviar campañas' },
      { key: 'marketing_campaigns', label: 'Gestionar campañas', desc: 'Administrar campañas' },
    ]
  },
  'Configuración': {
    icon: Settings,
    color: '#6B7280',
    description: 'Ajustes del sistema',
    permissions: [
      { key: 'settings_view', label: 'Ver configuración', desc: 'Acceso a ajustes' },
      { key: 'settings_edit', label: 'Editar configuración', desc: 'Modificar ajustes' },
      { key: 'settings_email', label: 'Configurar emails', desc: 'Ajustes de correo' },
      { key: 'settings_payments', label: 'Configurar pagos', desc: 'Ajustes de pagos' },
    ]
  },
  'Equipo': {
    icon: UserPlus,
    color: '#8B5CF6',
    description: 'Usuarios del equipo administrativo',
    permissions: [
      { key: 'users_view', label: 'Ver usuarios', desc: 'Lista del equipo' },
      { key: 'users_create', label: 'Crear usuarios', desc: 'Agregar al equipo' },
      { key: 'users_edit', label: 'Editar usuarios', desc: 'Modificar datos' },
      { key: 'users_delete', label: 'Eliminar usuarios', desc: 'Quitar del equipo' },
    ]
  },
  'Roles y Permisos': {
    icon: Shield,
    color: '#DC2626',
    description: 'Gestión de roles y permisos',
    permissions: [
      { key: 'roles_view', label: 'Ver roles', desc: 'Lista de roles' },
      { key: 'roles_create', label: 'Crear roles', desc: 'Agregar roles' },
      { key: 'roles_edit', label: 'Editar roles', desc: 'Modificar roles' },
      { key: 'roles_delete', label: 'Eliminar roles', desc: 'Borrar roles' },
      { key: 'roles_assign', label: 'Asignar roles', desc: 'Dar roles a usuarios' },
    ]
  },
  'Quiénes Somos': {
    icon: Users,
    color: '#14B8A6',
    description: 'Página institucional',
    permissions: [
      { key: 'about_view', label: 'Ver página', desc: 'Acceso a contenido' },
      { key: 'about_edit', label: 'Editar página', desc: 'Modificar contenido' },
    ]
  },
  'Logs y Reportes': {
    icon: Eye,
    color: '#64748B',
    description: 'Registros y reportes del sistema',
    permissions: [
      { key: 'logs_view', label: 'Ver logs', desc: 'Actividad del sistema' },
      { key: 'logs_export', label: 'Exportar logs', desc: 'Descargar registros' },
      { key: 'reports_view', label: 'Ver reportes', desc: 'Informes generales' },
      { key: 'reports_financial', label: 'Reportes financieros', desc: 'Informes de dinero' },
      { key: 'reports_export', label: 'Exportar reportes', desc: 'Descargar informes' },
    ]
  },
};

const initialPermissions = Object.values(PERMISSION_GROUPS).reduce((acc, group) => {
  group.permissions.forEach(p => { acc[p.key] = false; });
  return acc;
}, {});

const roleColors = [
  { value: '#D4AF37', label: 'Dorado', gradient: 'from-[#D4AF37] to-[#B8962E]' },
  { value: '#10B981', label: 'Verde', gradient: 'from-[#10B981] to-[#059669]' },
  { value: '#3B82F6', label: 'Azul', gradient: 'from-[#3B82F6] to-[#2563EB]' },
  { value: '#8B5CF6', label: 'Violeta', gradient: 'from-[#8B5CF6] to-[#7C3AED]' },
  { value: '#EF4444', label: 'Rojo', gradient: 'from-[#EF4444] to-[#DC2626]' },
  { value: '#F97316', label: 'Naranja', gradient: 'from-[#F97316] to-[#EA580C]' },
  { value: '#EC4899', label: 'Rosa', gradient: 'from-[#EC4899] to-[#DB2777]' },
  { value: '#06B6D4', label: 'Cyan', gradient: 'from-[#06B6D4] to-[#0891B2]' },
];

const roleIcons = [
  { value: 'Shield', label: 'Escudo', icon: Shield },
  { value: 'ShieldCheck', label: 'Verificado', icon: ShieldCheck },
  { value: 'Crown', label: 'Corona', icon: Crown },
  { value: 'Users', label: 'Usuarios', icon: Users },
  { value: 'Settings', label: 'Config', icon: Settings },
  { value: 'Eye', label: 'Vista', icon: Eye },
  { value: 'Sparkles', label: 'Estrella', icon: Sparkles },
  { value: 'Star', label: 'Favorito', icon: Star },
  { value: 'Zap', label: 'Rayo', icon: Zap },
  { value: 'Calendar', label: 'Calendario', icon: Calendar },
];

// Role Card Component
const RoleCard = ({ role, onEdit, onDuplicate, onDelete, usersCount, totalPermsCount }) => {
  const permCount = Object.values(role.permissions || {}).filter(Boolean).length;
  const permPercent = Math.round((permCount / totalPermsCount) * 100);
  const RoleIcon = roleIcons.find(i => i.value === role.icon)?.icon || Shield;
  const colorData = roleColors.find(c => c.value === role.color) || roleColors[0];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative bg-[#121212] border border-[#27272A] rounded-2xl overflow-hidden hover:border-[#3F3F46] transition-all duration-300"
      data-testid={`role-card-${role.id}`}
    >
      {/* Top gradient bar */}
      <div 
        className={`h-1.5 bg-gradient-to-r ${colorData.gradient}`}
      />
      
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center relative overflow-hidden"
              style={{ backgroundColor: `${role.color || '#D4AF37'}15` }}
            >
              <div 
                className="absolute inset-0 opacity-20"
                style={{ 
                  background: `radial-gradient(circle at 30% 30%, ${role.color}40, transparent 70%)`
                }}
              />
              <RoleIcon size={26} style={{ color: role.color || '#D4AF37' }} />
            </div>
            <div>
              <h3 className="font-bold text-lg text-white leading-tight">{role.name}</h3>
              {role.description && (
                <p className="text-sm text-[#71717A] line-clamp-1 mt-0.5">{role.description}</p>
              )}
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
              >
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-[#27272A] min-w-[160px]">
              <DropdownMenuItem onClick={() => onEdit(role)} className="cursor-pointer gap-2">
                <Edit size={14} />
                Editar rol
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(role)} className="cursor-pointer gap-2">
                <Copy size={14} />
                Duplicar
              </DropdownMenuItem>
              {!role.is_system && (
                <>
                  <DropdownMenuSeparator className="bg-[#27272A]" />
                  <DropdownMenuItem 
                    onClick={() => onDelete(role)}
                    className="cursor-pointer text-red-400 focus:text-red-400 gap-2"
                  >
                    <Trash2 size={14} />
                    Eliminar
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <UserCheck size={14} className="text-[#52525B]" />
            <span className="text-[#A1A1AA]">
              <span className="font-semibold text-white">{usersCount || 0}</span> usuarios
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Lock size={14} className="text-[#52525B]" />
            <span className="text-[#A1A1AA]">
              <span className="font-semibold" style={{ color: role.color || '#D4AF37' }}>{permCount}</span>/{totalPermsCount}
            </span>
          </div>
        </div>
        
        {/* Permission Progress */}
        <div className="space-y-2">
          <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
            <motion.div 
              className="h-full rounded-full"
              style={{ 
                background: `linear-gradient(90deg, ${role.color || '#D4AF37'}, ${role.color || '#D4AF37'}80)`
              }}
              initial={{ width: 0 }}
              animate={{ width: `${permPercent}%` }}
              transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
            />
          </div>
          <p className="text-xs text-[#52525B]">
            {permPercent}% de permisos activos
          </p>
        </div>

        {/* Quick Permission Preview */}
        <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-[#1F1F1F]">
          {Object.entries(PERMISSION_GROUPS).slice(0, 5).map(([name, group]) => {
            const count = group.permissions.filter(p => role.permissions?.[p.key]).length;
            const Icon = group.icon;
            const isActive = count > 0;
            return (
              <div
                key={name}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                  isActive ? '' : 'opacity-40'
                }`}
                style={{ 
                  backgroundColor: isActive ? `${group.color}15` : '#1A1A1A',
                  color: isActive ? group.color : '#52525B'
                }}
                title={`${name}: ${count}/${group.permissions.length}`}
              >
                <Icon size={11} />
                <span className="font-medium">{count}</span>
              </div>
            );
          })}
          {Object.keys(PERMISSION_GROUPS).length > 5 && (
            <div className="px-2 py-1 text-xs text-[#52525B]">
              +{Object.keys(PERMISSION_GROUPS).length - 5}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Permission Group Component for Modal
const PermissionGroup = ({ groupName, group, permissions, onChange, isExpanded, onToggleExpand }) => {
  const Icon = group.icon;
  const enabled = group.permissions.filter(p => permissions[p.key]).length;
  const total = group.permissions.length;
  const allEnabled = enabled === total;
  
  const toggleAll = (e) => {
    e.stopPropagation();
    const newValue = !allEnabled;
    group.permissions.forEach(p => onChange(p.key, newValue));
  };
  
  return (
    <div 
      className="border border-[#27272A] rounded-xl overflow-hidden transition-all"
      style={{ borderColor: enabled > 0 ? `${group.color}25` : undefined }}
    >
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#121212] transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${group.color}15` }}
          >
            <Icon size={18} style={{ color: group.color }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">{groupName}</span>
              <span 
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ 
                  backgroundColor: enabled > 0 ? `${group.color}20` : '#27272A',
                  color: enabled > 0 ? group.color : '#52525B'
                }}
              >
                {enabled}/{total}
              </span>
            </div>
            <span className="text-xs text-[#52525B]">{group.description}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleAll}
            className="text-xs h-8 px-3"
            style={{ color: allEnabled ? group.color : '#71717A' }}
          >
            {allEnabled ? <Unlock size={14} /> : <Lock size={14} />}
            <span className="ml-1.5 hidden sm:inline">{allEnabled ? 'Quitar' : 'Todos'}</span>
          </Button>
          <ChevronDown 
            size={16} 
            className={`text-[#52525B] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
          />
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-[#27272A]"
          >
            <div className="p-4 grid md:grid-cols-2 gap-2 bg-[#0A0A0A]/50">
              {group.permissions.map((perm) => (
                <label
                  key={perm.key}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    permissions[perm.key] 
                      ? 'bg-[#121212]' 
                      : 'hover:bg-[#121212]/50'
                  }`}
                  style={permissions[perm.key] ? { 
                    boxShadow: `inset 0 0 0 1px ${group.color}25` 
                  } : {}}
                >
                  <Switch
                    checked={permissions[perm.key] || false}
                    onCheckedChange={(checked) => onChange(perm.key, checked)}
                  />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium block ${permissions[perm.key] ? 'text-white' : 'text-[#A1A1AA]'}`}>
                      {perm.label}
                    </span>
                    <span className="text-xs text-[#52525B] truncate block">{perm.desc}</span>
                  </div>
                  {permissions[perm.key] && (
                    <Check size={14} style={{ color: group.color }} />
                  )}
                </label>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RolesPage = () => {
  const { isOwner } = useAuth();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#D4AF37',
    icon: 'Shield',
    permissions: { ...initialPermissions }
  });

  const totalPermsCount = Object.keys(initialPermissions).length;

  const fetchData = async () => {
    try {
      const [rolesRes, usersRes] = await Promise.all([
        rolesApi.getAll(),
        usersApi.getAll().catch(() => ({ data: [] }))
      ]);
      setRoles(rolesRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      fetchData();
    }
  }, [isOwner]);

  // Count users per role
  const usersPerRole = useMemo(() => {
    const counts = {};
    users.forEach(user => {
      if (user.role_id) {
        counts[user.role_id] = (counts[user.role_id] || 0) + 1;
      }
    });
    return counts;
  }, [users]);

  // Filter roles based on search and tab
  const filteredRoles = useMemo(() => {
    let filtered = roles;
    
    if (searchTerm) {
      filtered = filtered.filter(role => 
        role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (role.description || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (activeTab === 'system') {
      filtered = filtered.filter(role => role.is_system);
    } else if (activeTab === 'custom') {
      filtered = filtered.filter(role => !role.is_system);
    }
    
    return filtered;
  }, [roles, searchTerm, activeTab]);

  const handleOpenCreate = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      description: '',
      color: '#D4AF37',
      icon: 'Shield',
      permissions: { ...initialPermissions }
    });
    setExpandedGroups({});
    setModalOpen(true);
  };

  const handleOpenEdit = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      color: role.color || '#D4AF37',
      icon: role.icon || 'Shield',
      permissions: { ...initialPermissions, ...role.permissions }
    });
    setExpandedGroups({});
    setModalOpen(true);
  };

  const handleDuplicate = (role) => {
    setSelectedRole(null);
    setFormData({
      name: `${role.name} (copia)`,
      description: role.description || '',
      color: role.color || '#D4AF37',
      icon: role.icon || 'Shield',
      permissions: { ...initialPermissions, ...role.permissions }
    });
    setExpandedGroups({});
    setModalOpen(true);
    toast.info('Editá el rol duplicado y guardalo');
  };

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: value }
    }));
  };

  const toggleGroupExpand = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const selectAllPermissions = () => {
    const newPerms = {};
    Object.values(PERMISSION_GROUPS).forEach(group => {
      group.permissions.forEach(p => { newPerms[p.key] = true; });
    });
    setFormData(prev => ({ ...prev, permissions: newPerms }));
  };

  const clearAllPermissions = () => {
    setFormData(prev => ({ ...prev, permissions: { ...initialPermissions } }));
  };

  const getTotalPermissions = (perms) => {
    return Object.values(perms || {}).filter(Boolean).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        icon: formData.icon,
        permissions: formData.permissions
      };

      if (selectedRole) {
        await rolesApi.update(selectedRole.id, payload);
        toast.success('Rol actualizado correctamente');
      } else {
        await rolesApi.create(payload);
        toast.success('Rol creado correctamente');
      }

      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar rol');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;

    try {
      await rolesApi.delete(selectedRole.id);
      toast.success('Rol eliminado');
      setDeleteDialogOpen(false);
      setSelectedRole(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar rol');
    }
  };

  const filteredGroups = Object.entries(PERMISSION_GROUPS).filter(([name]) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOwner) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle size={40} className="text-red-500" />
              </div>
              <h3 className="text-2xl font-black uppercase mb-2">Acceso Restringido</h3>
              <p className="text-[#A1A1AA] max-w-md">
                Solo el administrador principal puede gestionar roles y permisos.
              </p>
            </div>
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="roles-manager" className="p-6 lg:p-8 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center shadow-lg shadow-[#D4AF37]/20">
                  <Shield size={28} className="text-black" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-black tracking-tight">Roles y Permisos</h1>
                  <p className="text-[#71717A] text-sm">Define qué puede hacer cada miembro del equipo</p>
                </div>
              </div>
              <Button 
                onClick={handleOpenCreate}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold px-6 h-11"
                data-testid="create-role-btn"
              >
                <Plus size={18} className="mr-2" />
                Crear Rol
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 hover:border-[#3F3F46] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center">
                    <Layers size={22} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white">{roles.length}</p>
                    <p className="text-xs text-[#71717A]">Roles totales</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 hover:border-[#3F3F46] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center">
                    <Lock size={22} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white">{totalPermsCount}</p>
                    <p className="text-xs text-[#71717A]">Permisos</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 hover:border-[#3F3F46] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                    <UserPlus size={22} className="text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white">{roles.filter(r => !r.is_system).length}</p>
                    <p className="text-xs text-[#71717A]">Personalizados</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5 hover:border-[#3F3F46] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex items-center justify-center">
                    <Users size={22} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-white">{users.length}</p>
                    <p className="text-xs text-[#71717A]">Usuarios del equipo</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2 bg-[#121212] border border-[#27272A] rounded-lg p-1">
                {[
                  { id: 'all', label: 'Todos', count: roles.length },
                  { id: 'system', label: 'Sistema', count: roles.filter(r => r.is_system).length },
                  { id: 'custom', label: 'Personalizados', count: roles.filter(r => !r.is_system).length },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-[#D4AF37] text-black'
                        : 'text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]'
                    }`}
                  >
                    {tab.label}
                    <span className={`ml-1.5 text-xs ${activeTab === tab.id ? 'text-black/70' : 'text-[#52525B]'}`}>
                      ({tab.count})
                    </span>
                  </button>
                ))}
              </div>
              
              <div className="relative w-full sm:w-64">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar roles..."
                  className="pl-9 bg-[#121212] border-[#27272A] focus:border-[#D4AF37] h-10"
                />
              </div>
            </div>

            {/* Roles Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 animate-pulse">
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-14 h-14 bg-[#27272A] rounded-xl" />
                      <div className="flex-1">
                        <div className="h-5 bg-[#27272A] rounded w-32 mb-2" />
                        <div className="h-3 bg-[#27272A] rounded w-48" />
                      </div>
                    </div>
                    <div className="h-2 bg-[#27272A] rounded-full" />
                  </div>
                ))}
              </div>
            ) : filteredRoles.length === 0 ? (
              <motion.div 
                className="bg-[#121212] border border-[#27272A] rounded-2xl text-center py-16"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <Shield size={40} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-black mb-2">
                  {searchTerm ? 'Sin resultados' : 'Sin Roles Creados'}
                </h3>
                <p className="text-[#71717A] mb-8 max-w-md mx-auto">
                  {searchTerm 
                    ? `No se encontraron roles con "${searchTerm}"`
                    : 'Los roles te permiten controlar qué puede hacer cada miembro del equipo.'
                  }
                </p>
                {!searchTerm && (
                  <Button onClick={handleOpenCreate} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold px-8">
                    <Plus size={18} className="mr-2" />
                    Crear Primer Rol
                  </Button>
                )}
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filteredRoles.map((role, idx) => (
                  <RoleCard
                    key={role.id}
                    role={role}
                    onEdit={handleOpenEdit}
                    onDuplicate={handleDuplicate}
                    onDelete={(role) => {
                      setSelectedRole(role);
                      setDeleteDialogOpen(true);
                    }}
                    usersCount={usersPerRole[role.id]}
                    totalPermsCount={totalPermsCount}
                  />
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="bg-[#0A0A0A] border border-[#27272A] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
            <DialogHeader className="p-6 border-b border-[#27272A] bg-gradient-to-r from-[#121212] to-transparent">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center relative overflow-hidden"
                  style={{ backgroundColor: `${formData.color}20` }}
                >
                  <div 
                    className="absolute inset-0 opacity-30"
                    style={{ 
                      background: `radial-gradient(circle at 30% 30%, ${formData.color}50, transparent 70%)`
                    }}
                  />
                  {(() => {
                    const SelectedIcon = roleIcons.find(i => i.value === formData.icon)?.icon || Shield;
                    return <SelectedIcon size={26} style={{ color: formData.color }} />;
                  })()}
                </div>
                <div>
                  <DialogTitle className="text-xl font-black">
                    {selectedRole ? 'Editar Rol' : 'Nuevo Rol'}
                  </DialogTitle>
                  <p className="text-sm text-[#71717A]">
                    {selectedRole ? 'Modificá los permisos y configuración' : 'Definí un nuevo rol para tu equipo'}
                  </p>
                </div>
              </div>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto">
                {/* Basic Info */}
                <div className="p-6 border-b border-[#27272A] bg-[#121212]/30">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-[#52525B] mb-4">Información Básica</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-5">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-[#71717A]">Nombre del Rol *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Ej: Editor de Contenido"
                        required
                        className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-[#71717A]">Descripción (opcional)</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Breve descripción del rol"
                        className="bg-[#0A0A0A] border-[#27272A] focus:border-[#D4AF37] h-11"
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-start gap-6">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-[#71717A]">Color</Label>
                      <div className="flex gap-2">
                        {roleColors.map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                            className={`w-9 h-9 rounded-lg border-2 transition-all ${
                              formData.color === color.value 
                                ? 'border-white scale-110 shadow-lg' 
                                : 'border-transparent hover:scale-105'
                            }`}
                            style={{ 
                              background: `linear-gradient(135deg, ${color.value}, ${color.value}90)`
                            }}
                            title={color.label}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-[#71717A]">Ícono</Label>
                      <div className="flex gap-2 flex-wrap">
                        {roleIcons.map((iconItem) => {
                          const IconComp = iconItem.icon;
                          return (
                            <button
                              key={iconItem.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, icon: iconItem.value }))}
                              className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                                formData.icon === iconItem.value 
                                  ? 'border-white bg-white/10' 
                                  : 'border-[#27272A] hover:border-[#52525B]'
                              }`}
                              title={iconItem.label}
                            >
                              <IconComp size={16} style={{ color: formData.color }} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Permissions */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wider text-[#52525B]">Permisos</h3>
                      <p className="text-xs text-[#71717A] mt-0.5">
                        {getTotalPermissions(formData.permissions)} de {totalPermsCount} seleccionados
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={selectAllPermissions} 
                        className="border-[#27272A] text-xs h-8"
                      >
                        <CheckCircle2 size={14} className="mr-1.5" />
                        Todos
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={clearAllPermissions} 
                        className="border-[#27272A] text-xs h-8"
                      >
                        <XCircle size={14} className="mr-1.5" />
                        Ninguno
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {Object.entries(PERMISSION_GROUPS).map(([groupName, group]) => (
                      <PermissionGroup
                        key={groupName}
                        groupName={groupName}
                        group={group}
                        permissions={formData.permissions}
                        onChange={handlePermissionChange}
                        isExpanded={expandedGroups[groupName]}
                        onToggleExpand={() => toggleGroupExpand(groupName)}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter className="p-5 border-t border-[#27272A] bg-[#121212]/50">
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ 
                        background: `linear-gradient(135deg, ${formData.color}, ${formData.color}80)`
                      }}
                    />
                    <span className="text-sm text-[#71717A]">
                      {getTotalPermissions(formData.permissions)} permisos seleccionados
                    </span>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting} 
                      className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold px-6"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Check size={16} className="mr-2" />
                          {selectedRole ? 'Guardar Cambios' : 'Crear Rol'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#121212] border border-[#27272A]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-black flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <Trash2 className="text-red-500" size={22} />
                </div>
                ¿Eliminar este rol?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#A1A1AA]">
                Esta acción no se puede deshacer. 
                {usersPerRole[selectedRole?.id] > 0 && (
                  <span className="block mt-2 text-amber-400">
                    Hay {usersPerRole[selectedRole?.id]} usuario(s) con este rol asignado.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#27272A]">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
                <Trash2 size={16} className="mr-2" />
                Eliminar Rol
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default RolesPage;
